import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Observable, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { LucideAngularModule, Calendar, CheckCircle } from 'lucide-angular';

import { Destroyer } from '@app/shared/abstract/destroyer';
import { EventTemplateService } from '@app/event-templates/shared/event-template.service';
import { EventTemplateModel } from '@app/event-templates/shared/event-template.model';
import { EventTemplateItemModel, EventTemplateItemModelUtils } from '@app/event-templates/shared/event-template-item.model';
import { EventService } from '../shared/event.service';
import { RouteParams } from '@app/shared/abstract/route-params';
import { SelectComponent } from '@app/partials/form/select/select.component';
import { ButtonComponent } from '@app/partials/form/button/button.component';
import { NotificationService } from '@app/shared/notification/notification.service';
import { BadgeComponent } from '@app/shared/components/badge/badge.component';

@Component({
  selector: 'app-template-selection-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    LucideAngularModule,
    SelectComponent,
    ButtonComponent,
    BadgeComponent
  ],
  template: `
    <div class="flex h-full flex-col">
      <div class="border-b border-gray-200 px-6 py-4 dark:border-gray-800">
        <h2 class="text-lg font-medium text-gray-800 dark:text-white/90">
          Apply Template to Event
        </h2>
        <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Select a template to apply to this event. This will copy the template's timeline items.
        </p>
      </div>

      <div class="flex-1 px-6 py-4">
        <div class="space-y-6">
          <!-- Template Selection -->
          <div class="relative" style="z-index: 10000;">
            <app-select
              [control]="templateControl"
              [choices]="templateChoices$"
              label="Select Template"
              placeholder="Choose a template to apply"
              [searchable]="true"
              searchPlaceholder="Search templates..."
            ></app-select>
          </div>
          
          <div class="overflow-y-auto" style="max-height: calc(100vh - 300px);">

          <!-- Template Preview -->
          @if (selectedTemplate) {
            <div class="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
              <div class="flex items-start gap-3">
                <lucide-icon [name]="CheckCircleIcon" [size]="20" class="mt-0.5 text-blue-600 dark:text-blue-400"></lucide-icon>
                <div class="flex-1">
                  <h3 class="text-sm font-medium text-blue-900 dark:text-blue-100">
                    {{ selectedTemplate.name }}
                  </h3>
                  
                  <div class="mt-2 flex items-center gap-2">
                    <app-badge 
                      [text]="selectedTemplate.event_type" 
                      [color]="getEventTypeColor(selectedTemplate.event_type)"
                    ></app-badge>
                  </div>

                  @if (selectedTemplate.description) {
                    <p class="mt-2 text-xs text-blue-700 dark:text-blue-300">
                      {{ selectedTemplate.description }}
                    </p>
                  }

                  @if (selectedTemplate.items && selectedTemplate.items.length > 0) {
                    <div class="mt-3 text-xs text-blue-600 dark:text-blue-400">
                      <lucide-icon [name]="CalendarIcon" [size]="12" class="mr-1 inline"></lucide-icon>
                      {{ selectedTemplate.items.length }} timeline item{{ selectedTemplate.items.length !== 1 ? 's' : '' }} will be added to this event
                    </div>
                  }
                </div>
              </div>
            </div>
          }

          @if (hasSelectedTemplate() && getSortedItems().length > 0) {
            <div class="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
              <h4 class="text-sm font-medium text-gray-800 dark:text-white/90 mb-3">
                Timeline Items Preview
              </h4>
              <div class="space-y-2 max-h-48 overflow-y-auto">
                @for (item of getSortedItems(); track item.id) {
                  <div class="flex items-center gap-2 text-xs">
                    <span class="inline-flex items-center rounded px-2 py-1 text-xs font-medium bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                      {{ formatOffsetDisplay(item) }}
                    </span>
                    <span class="text-gray-600 dark:text-gray-400">
                      {{ formatTimeOfDay(item.time_of_day) }}
                    </span>
                    <span class="text-gray-800 dark:text-white/90">
                      {{ item.name }}
                    </span>
                  </div>
                }
              </div>
            </div>
          }
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="border-t border-gray-200 px-6 py-4 dark:border-gray-800">
        <div class="flex justify-end gap-3">
          <app-button
            [label]="'Cancel'"
            [style]="'secondary'"
            (click)="cancel()"
          ></app-button>
          <app-button
            [label]="applying ? 'Applying...' : 'Apply Template'"
            [style]="'primary'"
            [disabled]="!hasSelectedTemplate() || applying"
            (click)="applyTemplate()"
          ></app-button>
        </div>
      </div>
    </div>
  `
})
export class TemplateSelectionFormComponent extends Destroyer implements OnInit {
  @Input() eventId!: number;
  @Output() templateApplied = new EventEmitter<EventTemplateModel>();
  @Output() cancelled = new EventEmitter<void>();

  protected readonly CalendarIcon = Calendar;
  protected readonly CheckCircleIcon = CheckCircle;

  templateControl = new FormControl(null);
  selectedTemplate: EventTemplateModel | null = null;
  applying = false;

  templateChoices$: Observable<{ value: number; label: string }[]>;

  constructor(
    private eventTemplateService: EventTemplateService,
    private eventService: EventService,
    private notificationService: NotificationService
  ) {
    super();

    this.templateChoices$ = this.eventTemplateService
      .getPage(
        new RouteParams({
          pageNum: 1,
          pageSize: 1000,
        }),
      )
      .pipe(
        map((res) => res.data),
        map((templates) =>
          templates.map((template) => ({
            value: template.id!,
            label: template.name || `Template ${template.id}`,
          })),
        ),
      );
  }

  ngOnInit(): void {
    this.templateControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((templateId) => {
        if (templateId) {
          this.loadTemplate(templateId);
        } else {
          this.selectedTemplate = null;
        }
      });
  }

  private loadTemplate(templateId: number): void {
    this.eventTemplateService
      .get(templateId.toString())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.selectedTemplate = response.data || response;
        },
        error: (error) => {
          console.error('Error loading template:', error);
          this.notificationService.error('Failed to load template');
          this.templateControl.setValue(null);
        },
      });
  }

  hasSelectedTemplate(): boolean {
    return this.selectedTemplate !== null;
  }

  getSortedItems(): any[] {
    if (!this.selectedTemplate?.items) return [];

    return [...this.selectedTemplate.items].sort((a, b) => {
      // First sort by days_offset, then by time
      if (a.days_offset !== b.days_offset) {
        return a.days_offset - b.days_offset;
      }
      return a.time_of_day.localeCompare(b.time_of_day);
    });
  }

  formatOffsetDisplay(item: EventTemplateItemModel): string {
    return EventTemplateItemModelUtils.formatOffsetDisplay(
      item.offset_amount,
      item.offset_unit,
      item.offset_direction
    );
  }

  formatTimeOfDay(timeString: string): string {
    return EventTemplateItemModelUtils.formatTimeOfDay(timeString);
  }

  getEventTypeColor(eventType: string): string {
    const colorMap: Record<string, string> = {
      'Wedding': 'success',
      'Corporate Event': 'primary',
      'Christmas Party': 'error',
      'Private Event': 'warning',
      'Tasting Event': 'info',
      'Open Day': 'secondary',
      'Sales Day': 'accent'
    };
    return colorMap[eventType] || 'secondary';
  }

  applyTemplate(): void {
    if (!this.selectedTemplate?.id || !this.eventId) return;

    this.applying = true;

    this.eventTemplateService
      .applyToEvent(this.selectedTemplate.id, this.eventId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.applying = false;
          this.notificationService.success(
            `Template "${this.selectedTemplate!.name}" applied to event successfully!`
          );
          this.templateApplied.emit(this.selectedTemplate!);
        },
        error: (error) => {
          this.applying = false;

          // Extract error message from backend response
          let errorMessage = 'Failed to apply template to event';
          if (error?.error?.message) {
            errorMessage = `Failed to apply template: ${error.error.message}`;
          } else if (error?.message) {
            errorMessage = `Failed to apply template: ${error.message}`;
          } else if (error?.statusText) {
            errorMessage = `Failed to apply template: ${error.statusText}`;
          }

          // Show error for longer duration (10 seconds) to ensure user sees it
          this.notificationService.error(
            errorMessage,
            'Template Application Failed',
            10000
          );

          console.error('Error applying template:', error);
        },
      });
  }

  cancel(): void {
    this.cancelled.emit();
  }
}